/**
 * The CRM-wide overdue-invoice warning.
 *
 * A full-bleed red bar pinned to the top of EVERY CRM page (rendered once by
 * `crm-layout`, sticky under the dashboard header) whenever the actor has
 * invoices past their due date. It is deliberately hard to ignore — a pulsing
 * alert icon, a live count and the total still outstanding, and the age of the
 * oldest debt — because money nobody chases is money nobody collects.
 *
 * It is **not dismissible**: the only way to make it go away is to settle the
 * invoices (or for them to stop being overdue). Clicking anywhere on the bar
 * opens `<OverdueInvoicesDialog>`, which lists every overdue invoice with a
 * one-click "Mark paid".
 *
 * Restraint, so "unmissable" doesn't become "unusable":
 *  - The pulse only runs at `critical` severity (30+ days late, or 5+ invoices);
 *    a single invoice a few days late gets the same bar, statically.
 *  - The whole animation is disabled under `prefers-reduced-motion: reduce`.
 *  - It renders NOTHING while loading and nothing at count 0 — no placeholder
 *    bar shifting the page on every navigation.
 *
 * Money: gated by the caller (the layout mounts the feed only for a viewer with
 * `invoices:access` + `financials:access`) AND redacted here through `<Money>`,
 * so the financial wall holds at the render site too. See components/money.tsx.
 */
import { Box, Button, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AlertTriangle, ChevronRight } from "lucide-react"

import { Money } from "@crm-src/components/money"
import type { OverdueSeverity, OverdueSummary } from "@crm-src/lib/overdue-invoices-api"

export interface OverdueInvoicesBannerProps {
  summary: OverdueSummary
  severity: OverdueSeverity
  onOpen: () => void
}

/** The headline sentence. Pure + exported so the copy is unit-testable. */
export function overdueHeadline(summary: OverdueSummary): string {
  const plural = summary.count === 1 ? "invoice is" : "invoices are"
  return `${summary.count} ${plural} overdue`
}

/** The supporting line: how old the worst one is, and what to do about it. */
export function overdueSubline(summary: OverdueSummary): string {
  if (summary.maxDaysOverdue <= 0) return "Review them and record any payments."
  const days = summary.maxDaysOverdue
  return `The oldest is ${days} day${days === 1 ? "" : "s"} past due. Click to review and mark them paid.`
}

export function OverdueInvoicesBanner({ summary, severity, onOpen }: OverdueInvoicesBannerProps) {
  if (summary.count <= 0) return null

  const critical = severity === "critical"

  return (
    <Box
      // A clickable REGION, not a button: the whole bar responds to a mouse
      // click for convenience, but the accessible control is the single "Review
      // & collect" button inside it. (Making the wrapper `role="button"` too
      // would nest one interactive control inside another, which screen readers
      // announce badly.) `role="status"` + polite live region means the warning
      // is announced when it appears without interrupting whatever the operator
      // is doing.
      role="status"
      aria-live="polite"
      onClick={onOpen}
      data-testid="overdue-invoices-banner"
      data-severity={severity}
      sx={{
        position: "sticky",
        zIndex: 9,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
        mb: 1,
        cursor: "pointer",
        borderRadius: 1,
        border: 1,
        borderColor: "error.dark",
        color: "common.white",
        bgcolor: "error.main",
        backgroundImage: (t) =>
          `linear-gradient(90deg, ${t.palette.error.dark} 0%, ${t.palette.error.main} 100%)`,
        boxShadow: (t) => `0 2px 10px ${alpha(t.palette.error.main, 0.45)}`,
        transition: "box-shadow 150ms, transform 150ms",
        "&:hover": {
          boxShadow: (t) => `0 4px 16px ${alpha(t.palette.error.main, 0.6)}`,
        },
        "&:focus-within": {
          outline: (t) => `2px solid ${t.palette.common.white}`,
          outlineOffset: 2,
        },
        // The nag. Only at critical severity, and never for a viewer who asked
        // the OS for less motion.
        ...(critical
          ? {
              animation: "overdueNag 2.4s ease-in-out infinite",
              "@keyframes overdueNag": {
                "0%, 92%, 100%": { boxShadow: "0 2px 10px rgba(0,0,0,0.25)" },
                "96%": { boxShadow: "0 0 0 4px rgba(255,255,255,0.35)" },
              },
            }
          : {}),
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          "& .overdue-pulse": { animation: "none" },
        },
      }}
      style={{ top: "var(--app-header-height)" }}
    >
      <Box
        className="overdue-pulse"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 32,
          width: 32,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: (t) => alpha(t.palette.common.white, 0.18),
          ...(critical
            ? {
                animation: "overduePulse 1.4s ease-in-out infinite",
                "@keyframes overduePulse": {
                  "0%, 100%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(1.14)", opacity: 0.75 },
                },
              }
            : {}),
        }}
      >
        <AlertTriangle size={18} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 700, lineHeight: 1.3 }}
        >
          {overdueHeadline(summary)} ·{" "}
          <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
            <Money value={summary.totalAmount} options={{ decimals: 0 }} /> outstanding
          </Box>
        </Typography>
        <Typography
          sx={{
            fontSize: 12.5,
            lineHeight: 1.4,
            color: (t) => alpha(t.palette.common.white, 0.85),
            display: { xs: "none", sm: "block" },
          }}
        >
          {overdueSubline(summary)}
        </Typography>
      </Box>

      <Button
        size="small"
        variant="contained"
        color="inherit"
        endIcon={<ChevronRight size={16} />}
        onClick={(event) => {
          event.stopPropagation()
          onOpen()
        }}
        aria-label={`${overdueHeadline(summary)}. Review and collect.`}
        data-testid="overdue-invoices-banner-cta"
        sx={{
          flexShrink: 0,
          bgcolor: "common.white",
          color: "error.dark",
          fontWeight: 700,
          whiteSpace: "nowrap",
          "&:hover": { bgcolor: (t) => alpha(t.palette.common.white, 0.88) },
        }}
      >
        Review &amp; collect
      </Button>
    </Box>
  )
}

export default OverdueInvoicesBanner
